import {
  HostListener,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { from, Subscription, timer } from 'rxjs';
import { IonInput } from '@ionic/angular';

import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { ScreenOrientation as Orientation } from '@capacitor/screen-orientation';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FrontendKeyboardService } from '@tablesready/frontend-keyboard';

@Component({
  selector: 'frontend-dropdown',
  styleUrls: ['./frontend-dropdown.component.scss'],
  templateUrl: './frontend-dropdown.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FrontendDropdownComponent),
      multi: true,
    },
  ],
})
export class FrontendDropdownComponent<T extends Record<string, any>> implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @ViewChild('mobileSearchBar') mobileSearchBar?: IonInput;
  @ViewChild('dropdownPanel') dropdownPanel!: TemplateRef<any>;
  @ViewChild('searchField') searchField?: IonInput;
  @ViewChild('sectionRef') sectionRef?: ElementRef;

  @Output() itemClear = new EventEmitter<T>();
  @Output() itemsClear = new EventEmitter<void>();
  @Output() popoverOpen = new EventEmitter<void>();
  @Output() popoverClose = new EventEmitter<void>();
  @Output() searchTermChange = new EventEmitter<string>();

  @Input() forceDesktopDropdown = false;
  @Input() forceMobileDropdown = false;
  @Input() showAddTagOnlyIfNoResults = false;
  @Input() searchable = true;
  @Input() disableFiltering = false;
  @Input() customName?: (item: T) => string;
  @Input() customOptionHtml?: (item: T) => string;
  @Input() virtualScroll = false;
  @Input() inlineClearButton = true;

  @Input() items: T | string[] = [];
  @Input() maxSelectedItems?: number;
  @Input() clearAllText = 'Clear';
  @Input() elementId = 'default-dropdown-id';
  @Input() noItemsText = 'No items';
  @Input() multiple = false;
  @Input() placeholder = 'Select or type to search';
  @Input() addTag = false;
  @Input() titleCase = false;
  @Input() clearable = false;
  @Input() minWidth = '6rem';
  @Input() maxListHeight = '11rem';
  @Input() customClass = '';
  @Input() plainValues = false;

  @Input() bindLabel = 'name';
  @Input() bindValue = '_id';

  @Input() markFirstSuggested = false;
  @Input() suggestedPrefix = 'Suggested: ';

  @Input() allowDisablingOptions = false;
  @Input() disabledPropertyName = 'disabled';
  @Input() closeOnSelect = false;

  mappedItems: T[] = [];
  icon = AppIcon;
  isInverted = false;
  iconSize = AppIconSize;
  searchString = '';
  listContentRef?: ElementRef;
  displayItems: T[] = [];

  itemsIdToItemMap: Record<string, T> = {};
  itemsIdToLabelMap: Record<string, string> = {};
  itemsIdToStateMap: Record<string, boolean> = {};

  isMobilePopoverOpen = false;
  isMobileDevice = false;
  isNative = false;
  isMobileSafari = false;
  orientationListener?: PluginListenerHandle;
  onChange?: (value: string[] | string) => void;
  onTouched?: () => void;
  value: string[] = [];
  disabled = false;
  keyWaitTime = 1000;
  currentKeys: string[] = [];

  private timerSubs: Subscription[] = [];
  private overlayRef?: OverlayRef;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly keyboardService: FrontendKeyboardService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly overlay: Overlay,
  ) {
    this.isNative = Capacitor.isNativePlatform();

    if (
      this.deviceDetectorService.browser === 'Safari' &&
      this.deviceDetectorService.deviceType !== 'desktop'
    ) {
      this.isMobileSafari = true;
    }

    if (
      Capacitor.isNativePlatform() ||
      this.isMobileSafari ||
      this.deviceDetectorService.isMobile() ||
      this.deviceDetectorService.isTablet()
    ) {
      this.isMobileDevice = true;
    }
  }

  get searchVisible(): boolean {
    return (
      this.multiple ||
      (!this.multiple && this.value?.length === 0) ||
      (this.addTag && this.value?.length === 0)
    );
  }

  presentDropdown(): void {
    const desktopDropdownOpen = this.overlayRef && this.overlayRef.hasAttached();
    const mobileDropdownOpen = this.isNative && this.isMobilePopoverOpen && !this.multiple;
    if (desktopDropdownOpen || mobileDropdownOpen) {
      return;
    }

    if (
      !this.isMobileDevice ||
      this.forceDesktopDropdown ||
      (!this.multiple && !this.addTag && !this.forceMobileDropdown)
    ) {
      void this.presentDesktopDropdown();
      return;
    }

    this.isMobilePopoverOpen = true;
    this.popoverOpen.emit();

    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  @HostListener('document:keydown.enter', ['$event'])
  onClick(): void {
    if (this.searchString?.length > 0) {
      const itemLabels = this.mappedItems.map(item => item[this.bindLabel]);
      if (this.addTag && !itemLabels.includes(this.searchString)) {
        this.addNewItem();
      }

      if (this.displayItems.length > 0) {
        const item = this.displayItems[0];
        if (this.multiple) {
          this.checkboxChange(item[this.bindValue], { checked: true });
        } else {
          this.selectSingleItem(item[this.bindValue]);
        }
      }

      this.searchString = '';
    }
  }

  dismissPopover(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
      this.overlayRef = undefined;
    }

    this.isMobilePopoverOpen = false;
    this.popoverClose.emit();
    this.cdr.detectChanges();
  }

  async presentDesktopDropdown() {
    this.dismissPopover();

    const anchor = this.sectionRef!.nativeElement as HTMLElement;
    const triggerRect = anchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    const defaultFontSize = 16;
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || defaultFontSize;
    let panelHeight = 200;

    if (this.maxListHeight.endsWith('rem')) {
      panelHeight = parseFloat(this.maxListHeight) * rootFontSize;
    } else if (this.maxListHeight.endsWith('px')) {
      panelHeight = parseFloat(this.maxListHeight);
    }

    const invertFirst = spaceBelow < panelHeight && spaceAbove > spaceBelow;
    const positions: ConnectedPosition[] = invertFirst
      ? [
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetX: 0,
          offsetY: -2,
        },
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 0,
          offsetY: 2,
        },
      ]
      : [
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 0,
          offsetY: 2,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetX: 0,
          offsetY: -2,
        },
      ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(anchor)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withViewportMargin(16)
      .withPositions(positions);

    positionStrategy.positionChanges.subscribe(change => {
      this.isInverted = change.connectionPair.overlayY === 'bottom';
      this.cdr.markForCheck();
    });

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'ion-transparent-overlay-backdrop',
      width: Math.round(triggerRect.width) + 1,
    });

    this.overlayRef.backdropClick().subscribe(() => this.dismissPopover());
    this.overlayRef.attach(new TemplatePortal(this.dropdownPanel, this.viewContainerRef));

    if (this.isNative && this.multiple) {
      return;
    }

    void this.searchField?.setFocus();
  }

  keySearch = (key: KeyboardEvent): void => {
    if (this.multiple) {
      return;
    }

    this.keySearchByKey(
      key,
      this.displayItems,
      this.bindLabel,
      this.bindValue,
      this.listContentRef?.nativeElement,
    );
  };

  clearSearchTermListener = (click: MouseEvent | TouchEvent): void => {
    const target = click.target as HTMLElement;
    if (!this.sectionRef?.nativeElement.contains(target)) {
      this.searchString = '';
      this.filterDisplayItems();
    }
  };

  ngOnInit(): void {
    document.addEventListener('keyup', this.keySearch);
    document.addEventListener('click', this.clearSearchTermListener);

    from(Orientation.addListener('screenOrientationChange', () => {
      this.dismissPopover();
    }))
      .subscribe((orientationListener) => {
        this.orientationListener = orientationListener;
      });

    const keySub = this.keyboardService.keyboardVisible$
      .subscribe(() => {
        this.overlayRef?.updatePosition();
      });
    this.subscriptions.add(keySub);
  }

  resetFocus(): void {
    (document.activeElement as any).blur();
  }

  inputValueChange(): void {
    this.filterDisplayItems();
    this.searchTermChange.emit(this.searchString);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['items'] && this.items && Object.keys(this.items).length)) {
      this.mapItemsInCaseOfPlainValue();
      this.generateMaps();
      this.filterDisplayItems();
      this.overlayRef?.updatePosition();

      setTimeout(() => {
        this.overlayRef?.updatePosition();
      });
    }
  }

  clearAll(): void {
    if (!this.clearable) {
      return;
    }

    this.isMobilePopoverOpen = false;
    this.popoverClose.emit();
    if (!this.multiple) {
      this.selectSingleItem('');
      this.value = [];
    }

    if (this.multiple) {
      this.value = [];
      this.checkboxChangeInternal();
    }

    this.generateMaps();
    this.filterDisplayItems();
  }

  resetItems(): void {
    this.items = [];
    this.mapItemsInCaseOfPlainValue();
    this.generateMaps();
    this.filterDisplayItems();
  }

  generateMaps(): void {
    this.itemsIdToStateMap = {};
    this.itemsIdToLabelMap = {};
    this.itemsIdToItemMap = {};

    if (!this.mappedItems) {
      return;
    }

    this.mappedItems.forEach((item, index: number) => {
      this.generateMapForSingleValue(item, index);
    });
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();

    const leftoverModal = document.getElementById(`dropdown-modal-${this.elementId}`);
    if (leftoverModal) {
      leftoverModal.remove();
    }

    this.isMobilePopoverOpen = false;
    this.popoverClose.emit();
    document.removeEventListener('keyup', this.keySearch);
    document.removeEventListener('click', this.clearSearchTermListener);
    void this.orientationListener?.remove();
    this.subscriptions.unsubscribe();
  }

  addNewItem = (): void => {
    if (this.maxSelectedItems && this.maxSelectedItems <= this.value?.length) {
      this.searchString = '';
      return;
    }

    if (!this.items.includes(this.searchString)) {
      this.items.push(this.searchString);
    }

    this.mapItemsInCaseOfPlainValue();

    if (this.multiple) {
      this.checkboxChange(this.searchString, { checked: true });
    } else {
      this.selectSingleItem(this.searchString);
    }

    if (this.overlayRef?.hasAttached()) {
      //void this.searchField?.setFocus();
    }

    this.searchString = '';
    this.cdr.detectChanges();
  };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: string[] | string): void {
    this.value = typeof value === 'object' ? value : [value];

    if (!value || value === '') {
      this.value = [];
    }

    if (!this.items) {
      this.items = [];
    }

    const itemsIds = this.items.map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      return item[this.bindValue];
    });

    this.value.forEach((val: string) => {
      if (itemsIds.includes(val)) {
        return;
      }

      this.items.push(val);
    });

    this.mapItemsInCaseOfPlainValue();
    this.generateMaps();
    this.filterDisplayItems();
  }

  selectSingleItem = (itemId: string): void => {
    if (this.disabled) {
      return;
    }

    this.searchString = '';
    if (this.multiple) {
      return;
    }

    this.value = [itemId];
    if (this.onChange) {
      this.onChange(this.multiple ? [itemId] : itemId);
    }
    if (this.onTouched) {
      this.onTouched();
    }

    this.generateMaps();
    this.dismissPopover();
  };

  deleteLastItem(keyboard: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (
      !keyboard?.key ||
      keyboard.key.toLowerCase() !== 'backspace' ||
      !this.clearable ||
      this.searchString !== ''
    ) {
      return;
    }

    if (this.multiple && this.value.length > 0) {
      this.checkboxChange(this.value[this.value.length - 1], { checked: false });
    }

    if (!this.multiple) {
      this.value = [];
    }

    this.updateOverlayPosition();
    this.isMobilePopoverOpen = false;
    this.popoverClose.emit();
  }

  checkboxChange = <D extends { checked: boolean }>(id: string, eventDetail: D): void => {
    const isChecked = eventDetail.checked;

    if (this.disabled) {
      return;
    }

    if (!this.value) {
      this.value = [];
    }

    if (isChecked && !this.value.includes(id)) {
      this.value = [...this.value, id];
      this.searchString = '';
    }

    if (!isChecked) {
      this.value = this.value.filter(item => item !== id);
    }

    this.checkboxChangeInternal();

    if (this.closeOnSelect && isChecked) {
      this.dismissPopover();
      return;
    }

    if (this.searchField && !this.isMobilePopoverOpen) {
      void this.searchField.setFocus();
    }
  };

  noBubble(interaction: MouseEvent | TouchEvent): void {
    interaction.stopPropagation();
  }

  filterDisplayItems(): void {
    if (this.searchString?.length > 0 && !this.isMobilePopoverOpen) {
      this.presentDropdown();
    }

    if (!this.mappedItems) {
      return;
    }
  }

  private generateMapForSingleValue(item: T, index: number): void {
    const id = String(item[this.bindValue]);
    let label = String(item[this.bindLabel]);

    if (this.customOptionHtml) {
      label = this.customOptionHtml(item);
    }

    if (this.titleCase) {
      label = `${label[0].toUpperCase()}${label.substring(1, label.length)}`;
    }

    if (index === 0 && this.markFirstSuggested && !this.value.includes(id)) {
      label = `${this.suggestedPrefix}${label}`;
    }

    this.itemsIdToLabelMap[id] = label;
    this.itemsIdToItemMap[id] = item;
    if (this.value) {
      this.itemsIdToStateMap[id] = this.value.includes(id);
    }
  }

  private mapItemsInCaseOfPlainValue(): void {
    this.mappedItems = this.items.map((item: T | string) => {
      if (typeof item === 'string') {
        const finalItem: T = {} as T;
        (finalItem as any)[this.bindValue] = item;
        (finalItem as any)[this.bindLabel] = item;
        return finalItem;
      }

      return item;
    });
  }

  private updateOverlayPosition(): void {
    setTimeout(() => {
      if (this.overlayRef && this.overlayRef.hasAttached()) {
        this.overlayRef.updatePosition();
      }
    });
  }

  private keySearchByKey<T extends Record<string, unknown>>(
    keyboard: KeyboardEvent,
    displayItems: T[],
    bindLabel: string,
    bindValue: string,
    listWrapper: HTMLDivElement,
  ): void {
    if (!listWrapper) {
      return;
    }

    this.waitTimer(keyboard.key, (str) => {
      this.keySearchInternal(
        str,
        displayItems,
        listWrapper,
        item => String((item as T)[bindLabel]),
        item => String((item as T)[bindValue]),
      );
    });
  }

  private waitTimer(keyCode: string, callback: (str: string) => void): void {
    this.unsub();
    this.currentKeys.push(keyCode);

    const timerSub = timer(this.keyWaitTime)
      .subscribe(() => {
        callback(this.currentKeys.join(''));
        this.currentKeys = [];
      });
    const newSub = new Subscription();
    newSub.add(timerSub);
    this.timerSubs.push(newSub);
  }

  private keySearchInternal(
    searchString: string,
    displayItems: unknown[],
    listWrapper: HTMLDivElement,
    searchItem: (item: unknown) => string,
    searchElement: (item: unknown) => string,
  ): void {
    const foundItem = displayItems.find(item => searchItem((item))
      .toLowerCase()
      .startsWith(searchString.toLowerCase()));
    if (!foundItem) {
      return;
    }

    const targetItem = Object.values(listWrapper.children)
      .find(value => value.id === `display-item-${searchElement(foundItem)}`);
    if (!targetItem) {
      return;
    }

    targetItem.scrollIntoView();
  }

  unsub(): void {
    this.timerSubs.forEach((sub) => {
      sub.unsubscribe();
    });
    this.timerSubs = [];
  }

  private checkboxChangeInternal(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
    if (this.onTouched) {
      this.onTouched();
    }

    this.generateMaps();
    this.updateOverlayPosition();
  }
}
